/**
 * Zod schemas do funil comercial — lado web.
 *
 * `Lead` e `LeadActivity` espelham as tabelas `public.leads` e
 * `public.lead_activities` (migration 0011). Enums de `stage` e
 * `activity_type` ficam aqui como fonte canônica pro client —
 * componentes (badges, filtros) importam daqui em vez de hardcoding.
 */
import { z } from 'zod';

export const LEAD_STAGES = [
  'new',
  'form_sent',
  'form_answered',
  'proposal_sent',
  'proposal_accepted',
  'contract_sent',
  'signed',
  'provisioned',
  'lost',
] as const;
export const leadStageSchema = z.enum(LEAD_STAGES);
export type LeadStage = z.infer<typeof leadStageSchema>;

/** Rótulos em pt-BR exibidos na UI (badges, filtros). Mantido próximo
 * ao enum para evitar divergência. Se mudar stage no DB, atualiza aqui. */
export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  new: 'Novo',
  form_sent: 'Questionário enviado',
  form_answered: 'Questionário respondido',
  proposal_sent: 'Proposta enviada',
  proposal_accepted: 'Proposta aceita',
  contract_sent: 'Contrato enviado',
  signed: 'Contrato assinado',
  provisioned: 'Cliente ativo',
  lost: 'Perdido',
};

/** Versão curta dos rótulos — usada em badges densos (lista de leads,
 * kanban) onde o label completo gasta largura crítica. Mantém peso
 * visual via uppercase+bold (no caso de milestone) sem competir por
 * espaço horizontal. Reportado pelo Alexandre 2026-04-25:
 * "CONTRATO ASSINADO ocupa 200px na tabela; deixa só ASSINADO". */
export const LEAD_STAGE_LABELS_SHORT: Record<LeadStage, string> = {
  new: 'Novo',
  form_sent: 'Questionário',
  form_answered: 'Respondido',
  proposal_sent: 'Proposta',
  proposal_accepted: 'Aceita',
  contract_sent: 'Enviado',
  signed: 'Assinado',
  provisioned: 'Ativo',
  lost: 'Perdido',
};

/** Stages "ativos" — leads trabalháveis. Usado no filtro default
 * para esconder `provisioned` (já virou cliente) e `lost`. */
export const ACTIVE_LEAD_STAGES = LEAD_STAGES.filter(
  (s) => s !== 'provisioned' && s !== 'lost',
) as readonly LeadStage[];

export const LEAD_ACTIVITY_TYPES = [
  'note',
  'email_sent',
  'email_received',
  'call',
  'meeting',
  'form_sent',
  'form_viewed',
  'form_submitted',
  'proposal_sent',
  'proposal_viewed',
  'proposal_accepted',
  'proposal_rejected',
  'contract_sent',
  'contract_viewed_by_lead',
  'contract_signed',
  'contract_approved_by_lead',
  'contract_adjustment_requested',
  'provisioning_started',
  'invite_sent',
  'invite_resent',
  'invite_accepted',
  'provisioning_completed',
  'provisioning_failed',
  'stage_changed',
  'score_updated',
] as const;
export const leadActivityTypeSchema = z.enum(LEAD_ACTIVITY_TYPES);
export type LeadActivityType = z.infer<typeof leadActivityTypeSchema>;

/** Lista curada de especialidades — select fechado. Adicione aqui
 * quando Karla identificar nicho novo. "outro" permite texto livre
 * em `metadata.specialty_other`. */
export const MEDICAL_SPECIALTIES = [
  'clinica_geral',
  'pediatria',
  'ginecologia',
  'cardiologia',
  'dermatologia',
  'ortopedia',
  'psiquiatria',
  'psicologia',
  'nutricao',
  'odontologia',
  'fisioterapia',
  'outro',
] as const;

export const SPECIALTY_LABELS: Record<(typeof MEDICAL_SPECIALTIES)[number], string> = {
  clinica_geral: 'Clínica geral',
  pediatria: 'Pediatria',
  ginecologia: 'Ginecologia',
  cardiologia: 'Cardiologia',
  dermatologia: 'Dermatologia',
  ortopedia: 'Ortopedia',
  psiquiatria: 'Psiquiatria',
  psicologia: 'Psicologia',
  nutricao: 'Nutrição',
  odontologia: 'Odontologia',
  fisioterapia: 'Fisioterapia',
  outro: 'Outro',
};

/** Canais de origem do lead. Alinhado com a pergunta "Como conheceu" do
 * questionário (M1-01b). Karla também pode cadastrar manualmente um lead
 * com uma dessas origens ao criar no CRM. */
export const LEAD_SOURCES = [
  'indication',
  'google',
  'social_media',
  'event',
  'inbound_form',
  'manual',
  'other',
] as const;

export const LEAD_SOURCE_LABELS: Record<(typeof LEAD_SOURCES)[number], string> = {
  indication: 'Indicação',
  google: 'Google',
  social_media: 'Redes sociais',
  event: 'Evento',
  inbound_form: 'Formulário site',
  manual: 'Cadastro manual',
  other: 'Outro',
};

/** Motivos padronizados de "perdido". Karla seleciona ao arquivar o lead. */
export const LOST_REASONS = [
  'no_budget',
  'no_fit',
  'already_has_system',
  'no_response',
  'price',
  'timing',
  'other',
] as const;

export const LOST_REASON_LABELS: Record<(typeof LOST_REASONS)[number], string> = {
  no_budget: 'Sem orçamento no momento',
  no_fit: 'Perfil não combina com a plataforma',
  already_has_system: 'Já usa outro sistema',
  no_response: 'Sem resposta após tentativas',
  price: 'Considerou o preço alto',
  timing: 'Não é o momento',
  other: 'Outro motivo',
};

/**
 * Endereço cadastral do lead — espelha o shape que o BrasilAPI devolve
 * (cep, logradouro, número, complemento, bairro, município, UF). Todos
 * os campos opcionais pra permitir cadastro parcial e preenchimento
 * progressivo quando parte da informação ainda não está disponível.
 *
 * Guardado em `leads.address` como jsonb (migration 0038). Quando o
 * lead vira clínica (edge function `provision-clinic`), é copiado para
 * `clinics.address` sem transformação — o shape é idêntico no DS.
 */
export const leadAddressSchema = z
  .object({
    cep: z.string().max(10).nullable().optional(),
    street: z.string().max(200).nullable().optional(),
    number: z.string().max(20).nullable().optional(),
    complement: z.string().max(100).nullable().optional(),
    district: z.string().max(100).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    uf: z.string().max(2).nullable().optional(),
  })
  .passthrough();
export type LeadAddress = z.infer<typeof leadAddressSchema>;

export const leadSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string().min(1),
  /** Razão social legal — migration 0038. Usada em proposta/contrato. */
  legal_name: z.string().nullable(),
  clinic_name_guess: z.string().nullable(),
  email: z.string().email(),
  phone: z.string().nullable(),
  cnpj: z
    .string()
    .regex(/^[0-9]{14}$/)
    .nullable(),
  /** Endereço cadastral jsonb — migration 0038. */
  address: leadAddressSchema.default({}),
  specialty: z.string().nullable(),
  stage: leadStageSchema,
  source: z.string().nullable(),
  owner_id: z.string().uuid().nullable(),
  lost_reason: z.string().nullable(),
  notes: z.string().nullable(),
  score: z.number().int().min(0).max(100).nullable(),
  next_followup_at: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Lead = z.infer<typeof leadSchema>;

export const leadActivitySchema = z.object({
  id: z.string().uuid(),
  lead_id: z.string().uuid(),
  actor_id: z.string().uuid().nullable(),
  activity_type: leadActivityTypeSchema,
  description: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string(),
});
export type LeadActivity = z.infer<typeof leadActivitySchema>;

/** Input do form de criar/editar lead. CNPJ é opcional (pode cadastrar
 * sem CNPJ quando ainda não se conhece, ex: lead vindo de evento).
 * Quando informado, a UI faz autofill via BrasilAPI e popula
 * `legal_name` + `address` junto com `clinic_name_guess` e `phone`. */
export const createLeadInputSchema = z.object({
  full_name: z.string().trim().min(2, 'Nome muito curto').max(160),
  legal_name: z.string().trim().max(300).nullable().default(null),
  clinic_name_guess: z.string().trim().max(200).nullable().default(null),
  email: z.string().trim().toLowerCase().email('Email inválido'),
  phone: z.string().trim().max(40).nullable().default(null),
  cnpj: z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v === '' || /^[0-9]{14}$/.test(v), 'CNPJ deve ter 14 dígitos')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .default(null),
  address: leadAddressSchema.default({}),
  specialty: z.enum(MEDICAL_SPECIALTIES).nullable().default(null),
  source: z.enum(LEAD_SOURCES).default('manual'),
  notes: z.string().max(2_000).nullable().default(null),
});
export type CreateLeadInput = z.infer<typeof createLeadInputSchema>;

/** Marca lead como perdido. Motivo estruturado + texto livre opcional. */
export const markLostInputSchema = z.object({
  lead_id: z.string().uuid(),
  reason: z.enum(LOST_REASONS),
  reason_detail: z.string().max(500).nullable().default(null),
});
export type MarkLostInput = z.infer<typeof markLostInputSchema>;

/**
 * Conta leads por stage a partir de lista em memória. Fica em schemas
 * (não em queries) porque é pura — não importa o supabase client.
 * Permite que `leads.smoke.test.tsx` importe sem puxar `lib/supabase.ts`
 * pela cadeia (o step `Test` do CI não injeta VITE_SUPABASE_*).
 */
export function countLeadsByStage(leads: readonly Lead[]): Record<LeadStage, number> {
  const counts = Object.fromEntries(LEAD_STAGES.map((s) => [s, 0])) as Record<LeadStage, number>;
  for (const l of leads) counts[l.stage]++;
  return counts;
}
